import { useCallback, useEffect, useMemo, useState } from "react";
import { BookMarked, CheckCircle2, FlaskConical, LibraryBig, Scale, ShieldAlert } from "lucide-react";
import { ModuleGuide, useModuleCopy } from "./ModuleGuide";

import {
  loadNarrativePrinciples,
  loadProjectFrameworks,
  setProjectFrameworkPreference,
  type NarrativePrinciple,
  type ProjectFramework,
  type StudioAuth,
} from "../api/client";

type Props = { apiOrigin: string; projectId: string; auth: StudioAuth };

export function IntelligencePanel({ apiOrigin, projectId, auth }: Props) {
  const [principles, setPrinciples] = useState<NarrativePrinciple[]>([]);
  const copy = useModuleCopy();
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState<ProjectFramework[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selected = useMemo(() => new Map(frameworks.map((item) => [item.principleId, item.mode])), [frameworks]);

  const refresh = useCallback(async () => {
    const [nextPrinciples, nextFrameworks] = await Promise.all([
      loadNarrativePrinciples({ apiOrigin, auth }),
      loadProjectFrameworks({ apiOrigin, projectId, auth }),
    ]);
    setPrinciples(nextPrinciples);
    setFrameworks(nextFrameworks);
  }, [apiOrigin, auth, projectId]);

  useEffect(() => { void refresh().catch((cause) => setError(cause instanceof Error ? cause.message : "叙事知识加载失败。")).finally(() => setLoading(false)); }, [refresh]);

  async function choose(principle: NarrativePrinciple, mode: "selected" | "disabled") {
    setBusyId(principle.id);
    setError(null);
    try {
      await setProjectFrameworkPreference({
        apiOrigin,
        projectId,
        principleId: principle.id,
        mode,
        rationale: mode === "selected" ? "作者在 Story Studio 中明确选择，作为建议性 Craft Control。" : "作者明确关闭该框架。",
        auth,
      });
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "框架设置失败。");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="intelligence-panel" aria-label="叙事智能">
      <header className="intelligence-header">
        <div><span className="eyebrow">NARRATIVE INTELLIGENCE · L0—L3</span><h1>叙事智能</h1><p>经典作品、写作技法与理论的可追溯建议；理论不是 Story Law。</p></div>
        <div className="knowledge-legend"><span><LibraryBig />Public only</span><span><Scale />Rights-aware</span><span><FlaskConical />Cross-work evaluated</span></div>
      </header>
      {error ? <p className="inline-error" role="alert">{error}</p> : null}
      {loading || busyId ? <p role="status">{loading ? copy.loading : copy.working}</p> : !error && principles.length === 0 ? <p>{copy.empty}</p> : null}
      <ModuleGuide module="intelligence" />
      <div className="principle-grid">
        {principles.map((principle) => {
          const mode = selected.get(principle.id);
          return <article className={`principle-card${mode === "selected" ? " is-selected" : ""}`} key={principle.id}>
            <div className="principle-meta"><span>{principle.layer}</span><span>{principle.knowledgeClass}</span><strong>{Math.round(principle.confidence * 100)}% confidence</strong></div>
            <h2>{principle.framework}</h2>
            <p className="principle-claim">{principle.claim}</p>
            <div className="mechanism"><BookMarked /><span><b>机制</b>{principle.mechanism}</span></div>
            <div className="principle-limits"><ShieldAlert /><div><b>风险与反例</b><p>{[...principle.risks, ...principle.counterexamples].slice(0, 2).join("；")}</p></div></div>
            {principle.crossWorkEvaluation ? <p className="cross-work"><FlaskConical />{principle.crossWorkEvaluation.workCount} 部代表作品 · {principle.crossWorkEvaluation.result} · {Math.round(principle.crossWorkEvaluation.score * 100)}%</p> : <p className="cross-work is-theory"><Scale />理论视角，未经跨作品泛化</p>}
            <div className="principle-actions">
              <button type="button" disabled={busyId === principle.id || mode === "disabled"} onClick={() => { void choose(principle, "disabled"); }}>本项目禁用</button>
              <button type="button" disabled={busyId === principle.id || mode === "selected"} onClick={() => { void choose(principle, "selected"); }}><CheckCircle2 />{mode === "selected" ? "已明确选择" : "选为 Craft Control"}</button>
            </div>
          </article>;
        })}
      </div>
    </main>
  );
}
